// web/src/components/records/BreakGlassDialog.tsx — the "Break the glass"
// consequence dialog (content-guide §5.2). Names the consequence before the verb,
// lists what happens, and takes a justification recorded in the audit. On confirm
// it convenes the unseal ceremony and routes to /unseal/:id (useConveneUnseal).
import React, { useState } from 'react';
import { Zap } from 'lucide-react';
import Modal from '../Modal';
import { Icon } from '../ui/Icon';
import { FeedbackMessage } from '../ui';
import type { ConveneTarget } from './useConveneUnseal';
import { recordName, windowLabel } from '../../lib/records';

export function BreakGlassDialog({
  target,
  timeoutSeconds,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  target: ConveneTarget;
  timeoutSeconds: number;
  busy: boolean;
  error: string | null;
  onConfirm: (justification: string) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [reason, setReason] = useState('');
  const name = recordName(target);
  const canSend = reason.trim().length > 0 && !busy;

  return (
    <Modal
      title="Break the glass"
      subtitle="Open an unseal request"
      maxWidth={480}
      dismissable={!busy}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onConfirm(reason.trim())}
            disabled={!canSend}
          >
            {busy ? <span className="spinner" aria-hidden="true" /> : <Icon icon={Zap} size="sm" />}
            Send request
          </button>
        </>
      }
    >
      <p style={{ color: 'var(--color-text2)', marginTop: 0, lineHeight: 1.5 }}>
        You're asking {target.thresholdK} of {target.totalN} members to help unseal{' '}
        <strong style={{ color: 'var(--color-text)' }}>{name}</strong>. They'll be notified and
        can see that you made this request. Once {target.thresholdK} shards arrive, the secret
        appears in your browser and a window of {windowLabel(timeoutSeconds)} begins.
      </p>

      <ul className="bgConseqList">
        <li>Members are notified and asked to submit their shards.</li>
        <li>This request is recorded in the audit trail, with your name and the reason.</li>
        <li>When the glass breaks, treat the secret as exposed and rotate it afterward.</li>
      </ul>

      <label className="field-label" htmlFor="bg-reason">
        Why do you need this? (recorded in the audit trail)
      </label>
      <textarea
        id="bg-reason"
        className="input"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={busy}
        placeholder="Production incident #4821 — rotating the leaked credential"
      />

      {error && <FeedbackMessage ok={false} msg={error} />}
    </Modal>
  );
}

export default BreakGlassDialog;
